/**
 * SmallBiz Innovation Color Palettes
 * Core brand color scales - the single source of truth for all colors
 *
 * Migrated from libs/brand-data/palettes.ts
 */

/**
 * SBS Brand Color Scales
 * Each scale runs from 900 (darkest) to 50 (lightest)
 */
export const sbs = {
  blue: {
    900: '#000A27',
    800: '#011B4A',
    700: '#032C71',
    600: '#073F99',
    500: '#0C52C4',
    400: '#1167F1',
    300: '#4A89F7',
    200: '#77A8FC',
    100: '#A4C6FF',
    50: '#D1E3FF',
  },
  storm: {
    900: '#00051C',
    800: '#000929',
    700: '#000F37',
    600: '#001546',
    500: '#1C3460',
    400: '#3D5379',
    300: '#617394',
    200: '#8695AE',
    100: '#ADB7C9',
    50: '#D5DAE4',
  },
  magenta: {
    900: '#33040F',
    800: '#5D0D22',
    700: '#8C1937',
    600: '#BD254E',
    500: '#F13265',
    400: '#F9607D',
    300: '#FE8698',
    200: '#FFA4B0',
    100: '#FFC3CA',
    50: '#FFE1E4',
  },
  sunshine: {
    900: '#362D07',
    800: '#635414',
    700: '#947F23',
    600: '#C8AC33',
    500: '#FFDC44',
    400: '#FFE370',
    300: '#FFE992',
    200: '#FFEFAF',
    100: '#FFF4CB',
    50: '#FFFAE5',
  },
  emeraldIce: {
    900: '#033018',
    800: '#0A5931',
    700: '#15864C',
    600: '#20B669',
    500: '#2CE887',
    400: '#68ED9C',
    300: '#8EF1B0',
    200: '#ADF5C4',
    100: '#CAF9D8',
    50: '#E5FCEB',
  },
  grayscale: {
    900: '#121212',
    800: '#252525',
    700: '#393939',
    600: '#4F4F4F',
    500: '#666666',
    400: '#7D7D7D',
    300: '#969696',
    200: '#AFAFAF',
    100: '#C9C9C9',
    50: '#E4E4E4',
  },
} as const;

/**
 * Primary Brand Colors
 * The core brand colors with their semantic roles
 */
export const primaryBrandColors = [
  {
    name: 'Blue 500',
    value: '#0C52C4',
    role: 'Primary actions, navigation, data highlights',
  },
  {
    name: 'Storm 600',
    value: '#001546',
    role: 'Depth, background anchors, typography on light surfaces',
  },
  {
    name: 'Sunshine 500',
    value: '#FFDC44',
    role: 'Calls-to-action and attention states',
  },
  {
    name: 'Magenta 500',
    value: '#F13265',
    role: 'Highlights, notifications, energetic accents',
  },
  {
    name: 'Emerald Ice 500',
    value: '#2CE887',
    role: 'Success, growth, sustainability messaging',
  },
] as const;

/**
 * Base neutral colors
 */
export const neutrals = {
  white: '#FFFFFF',
  black: '#000000',
  offWhite: '#FAFAFA',
  nearBlack: '#121212',
} as const;

// Type exports
export type SbsPalette = typeof sbs;
export type ColorScale = keyof SbsPalette;
export type ColorShade = keyof SbsPalette['blue'];
export type PrimaryBrandColor = (typeof primaryBrandColors)[number];
